import { Badge, LegacyCard, Text, Tooltip } from "@shopify/polaris"
import { observer } from "mobx-react"
import React from "react"

import { FormatPriceText, SimpleLine } from "@/components"
import stores from "@/stores"
import { formatDisplayNumber } from "@/utils/formatNumber"

interface IMetricRow {
  label: string
  tooltip: string
  value: React.ReactNode
}

interface ICardConfig {
  title: string
  tooltip: string
  value: React.ReactNode
  compare: number
  trend: number[]
  compareTrend: number[]
  rows: IMetricRow[]
}

const LabelWithTooltip = (props: {
  text: string
  tooltip: string
  variant: "headingSm" | "bodyMd"
}) => {
  const { text, tooltip, variant } = props
  return (
    <Tooltip content={tooltip} preferredPosition="above" hasUnderline>
      <Text variant={variant} as="span">
        {text}
      </Text>
    </Tooltip>
  )
}

const CompareBadge = (props: { compare: number }) => {
  const { compare } = props
  const isZero = compare === 0
  const isUp = compare > 0
  const arrow = isZero ? "" : isUp ? "↑ " : "↓ "
  return (
    <Badge tone={isUp ? "success" : undefined}>
      {`${arrow}${formatDisplayNumber(Math.abs(compare))}%`}
    </Badge>
  )
}

const DisplayList = (props: { hasCompare: boolean; loading: boolean }) => {
  const { hasCompare, loading } = props
  const { ReturnRequestsDataStore, UserInfoStore } = stores

  const currencySymbol = UserInfoStore.data.currency_format
  const currencyCode = UserInfoStore.data.currency
  const dailyData = ReturnRequestsDataStore.data || []

  const getTrend = (field: string) =>
    dailyData.map((item) => item.current?.[field] ?? 0)
  const getCompareTrend = (field: string) =>
    dailyData.map((item) => item.previous?.[field] ?? 0)

  const renderMoney = (value: number) => (
    <>
      <FormatPriceText
        value={value}
        currency={currencySymbol}
        hideDecimalsWhenInteger
      />{" "}
      {currencyCode}
    </>
  )
  const renderPercent = (value: number) =>
    `${formatDisplayNumber(Number(value || 0))}%`

  const cards: ICardConfig[] = [
    {
      title: t("TotalRetentionRevenue"),
      tooltip: t("TheTotalRevenueRetainedFromReturns"),
      value: renderMoney(ReturnRequestsDataStore.total_retention_revenue),
      compare: ReturnRequestsDataStore.total_retention_revenue_compare,
      trend: getTrend("retention_revenue"),
      compareTrend: getCompareTrend("retention_revenue"),
      rows: [
        {
          label: t("ExchangeUpsellRevenue"),
          tooltip: t("RevenueGainedWhenCustomersExchangeForHigherValueItem"),
          value: renderMoney(ReturnRequestsDataStore.exchange_upsell_value),
        },
        {
          label: t("NonCashRetentionRevenue"),
          tooltip: t("RevenueRetainedThroughNonCashMethods"),
          value: renderMoney(ReturnRequestsDataStore.total_no_cash),
        },
        {
          label: t("ReturnFeeRevenue"),
          tooltip: t("FeesCollectedFromReturnShippingAndRestocking"),
          value: renderMoney(ReturnRequestsDataStore.total_return_fee),
        },
      ],
    },
    {
      title: t("ItemReturnRate"),
      tooltip: t("ThePercentageOfItemsSoldThatResultedInAReturn"),
      value: renderPercent(ReturnRequestsDataStore.total_return_rate),
      compare: ReturnRequestsDataStore.total_return_rate_compare,
      trend: getTrend("return_rate"),
      compareTrend: getCompareTrend("return_rate"),
      rows: [
        {
          label: t("ExchangeRate"),
          tooltip: t("ThePercentageOfReturnedItemsResolvedThroughExchanges"),
          value: renderPercent(ReturnRequestsDataStore.total_exchange_rate),
        },
        {
          label: t("RefundRate"),
          tooltip: t("ThePercentageOfReturnedItemsRefundedThroughCashMethods"),
          value: renderPercent(ReturnRequestsDataStore.total_refund_rate),
        },
        {
          label: t("ReturnRequests"),
          tooltip: t("TotalNumberOfReturnRequestsDuringThisPeriod"),
          value: ReturnRequestsDataStore.requests_count,
        },
      ],
    },
  ]

  return (
    <div className="flex flex-col gap-4 md:flex-row">
      {cards.map((card) => (
        <div key={card.title} className="flex-1">
          <LegacyCard>
            <LegacyCard.Section>
              <div>
                <div>
                  <LabelWithTooltip
                    text={card.title}
                    tooltip={card.tooltip}
                    variant="headingSm"
                  />
                </div>
                <div className="flex items-end justify-between gap-2 mt-2">
                  <div className="flex items-end gap-2">
                    <Text variant="headingLg" as="p">
                      {card.value}
                    </Text>
                    <CompareBadge compare={card.compare} />
                  </div>
                  <div className="h-7 w-32 flex-shrink-0">
                    {!loading && (
                      <SimpleLine
                        data={card.trend}
                        compareData={hasCompare ? card.compareTrend : []}
                      />
                    )}
                  </div>
                </div>
                <div className="space-y-2 mt-3">
                  {card.rows.map((row) => (
                    <div
                      key={row.label}
                      className="flex items-center justify-between gap-2"
                    >
                      <LabelWithTooltip
                        text={row.label}
                        tooltip={row.tooltip}
                        variant="bodyMd"
                      />
                      <Text variant="bodyMd" as="span" fontWeight="medium">
                        {row.value}
                      </Text>
                    </div>
                  ))}
                </div>
              </div>
            </LegacyCard.Section>
          </LegacyCard>
        </div>
      ))}
    </div>
  )
}

export default observer(DisplayList)
